import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type { RuntimeProfileSummary } from "@app/types/assistant/agent";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher, SWRConfiguration } from "swr";

export type ComputerType = {
  sId: string;
  name: string;
  version: string;
  runtimes: RuntimeProfileSummary[];
};

type GetComputerResponseBody = { computer: ComputerType | null };

function buildComputersUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/computers`;
}

export function useComputer({
  owner,
  disabled,
  refreshInterval,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
  refreshInterval?: SWRConfiguration<GetComputerResponseBody>["refreshInterval"];
}) {
  const { fetcher } = useFetcher();
  const computerFetcher: Fetcher<GetComputerResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildComputersUrl(owner.sId),
    computerFetcher,
    { disabled, refreshInterval }
  );

  return {
    computer: data ? data.computer : undefined,
    isComputerLoading: !disabled && !error && !data,
    isComputerError: !!error,
    mutateComputer: mutate,
  };
}

export function useCreatePairingCode({ owner }: { owner: LightWorkspaceType }) {
  const sendNotification = useSendNotification();

  return useCallback(async (): Promise<string | null> => {
    try {
      const res = await clientFetch(
        `${buildComputersUrl(owner.sId)}/pairing_code`,
        { method: "POST" }
      );
      if (!res.ok) {
        const errorData = await getErrorFromResponse(res);
        sendNotification({
          type: "error",
          title: "Could not create a pairing code",
          description: errorData.message,
        });
        return null;
      }
      const { code } = (await res.json()) as { code: string };
      return code;
    } catch (e) {
      sendNotification({
        type: "error",
        title: "Could not create a pairing code",
        description: normalizeError(e).message,
      });
      return null;
    }
  }, [owner.sId, sendNotification]);
}

export function useDisconnectComputer({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();

  return useCallback(async (): Promise<boolean> => {
    try {
      const res = await clientFetch(buildComputersUrl(owner.sId), {
        method: "DELETE",
      });
      if (!res.ok) {
        const errorData = await getErrorFromResponse(res);
        sendNotification({
          type: "error",
          title: "Could not disconnect your computer",
          description: errorData.message,
        });
        return false;
      }
      return true;
    } catch (e) {
      sendNotification({
        type: "error",
        title: "Could not disconnect your computer",
        description: normalizeError(e).message,
      });
      return false;
    }
  }, [owner.sId, sendNotification]);
}

export function useSetAgentRuntimeProfile({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();

  return useCallback(
    async (agentId: string, profile: string | null): Promise<boolean> => {
      try {
        const res = await clientFetch(
          `/api/w/${owner.sId}/assistant/agent_configurations/${agentId}/external_runtime_profile`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ profile }),
          }
        );
        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: "Could not change how your agent runs",
            description: errorData.message,
          });
          return false;
        }
        return true;
      } catch (e) {
        sendNotification({
          type: "error",
          title: "Could not change how your agent runs",
          description: normalizeError(e).message,
        });
        return false;
      }
    },
    [owner.sId, sendNotification]
  );
}
